import { JSX } from "react";
import { useTheme } from "styled-components";
import { Box } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { ITheme, RecursiveCSS } from "../theme/GlobalTheme";

export type TButtons = typeof getDefaultButtons;
export type TButtonVariant = keyof TButtons;

const defaultButton: RecursiveCSS = {
	padding: "14px 14px",
	borderRadius: "6px",
	cursor: "pointer",
	boxShadow: "0px 0px 10px rgba(0,0,0,.1)",
	transition: "background-color .2s, border .2s",
	display: "flex",
	alignItems: "center",
	position: "relative",
	gap: "10px",
	fontWeight: "400",
	userSelect: "none",
	fontSize: ".95rem",
	justifyContent: "center",
	whiteSpace: "nowrap",
	textOverflow: "ellipsis",
	overflow: "hidden",
	backgroundColor: "buttonBackgroundPrimary",
	color: "buttonTextPrimary",

	"&:hover": {
		transition: "background-color .2s, border .2s",
		backgroundColor: "rgba(0,0,0,.1)",
	},
};

const secondary: RecursiveCSS = {
	...defaultButton,
	backgroundColor: "transparent",
	border: "1px solid rgba(127,116,116,.3)",
	boxShadow: "none",
	fontWeight: "400",
	color: "buttonTextSecondary",
	"&:hover": {
		backgroundColor: "rgba(0,0,0,.02)",
	},
};

const small: RecursiveCSS = {
	fontSize: "13px",
	padding: "10px 16px",
};

export const getDefaultButtons = {
	primary: {
		...defaultButton,
		"&:hover": {
			transition: "background-color .2s, border .2s",
			backgroundColor: "buttonBackgroundPrimary",
		},
	},

	secondary: { ...secondary },

	empty: {
		...defaultButton,
		boxShadow: "none",
		backgroundColor: "transparent",
		"&:hover": {
			backgroundColor: "transparent",
		},
	},

	icon: {
		...defaultButton,
		//border: "1px solid buttonBackgroundSecondary",
		boxShadow: "none",
		backgroundColor: "transparent",
		"&:hover": {
			backgroundColor: "transparent", // "buttonBackgroundSecondary",
			//border: "1px solid buttonBackgroundPrimary",
		},
		borderRadius: "8px",
		padding: "0px",
		display: "flex",
	},

	primarySmall: {
		...defaultButton,
		...small,
		"&:hover": {
			transition: "background-color .2s, border .2s",
			backgroundColor: "buttonBackgroundPrimary",
		},
	},

	dangerSmall: {
		...defaultButton,
		...small,
		backgroundColor: "#EA3568",
		color: "#FFFFFF",
		"&:hover": {
			transition: "background-color .2s, border .2s",
			backgroundColor: "#C93052",
		},
	},

	secondarySmall: {
		...secondary,
		...small,
	},

	emptySmall: { ...defaultButton },
};

interface IButtonProps {
	onClick: (e?: any) => void;
	children?: any;
	title?: string;
	variant?: TButtonVariant;
	sx?: RecursiveCSS;
	iconRight?: JSX.Element;
	size?: "small" | "medium" | "large";
	disabled?: boolean;
	warning?: boolean;
	color?: string;
	decorator?: "form";
	icon?: JSX.Element;
	center?: boolean;
}

const Button = ({ onClick, icon, children, variant, sx, iconRight, disabled, title, center }: IButtonProps) => {
	if (!variant && icon && !title && !children) variant = "icon";

	if (!variant) variant = "primary";

	let tsx = {} as RecursiveCSS;

	if (!children && title) children = title;
	let content = children;

	const theme = useTheme() as ITheme;
	if (theme && theme.buttons && theme.buttons[variant]) tsx = theme.buttons[variant];

	return (
		<Box
			sx={{
				...tsx, //variant
				...sx, // user styles
				pointerEvents: disabled ? "none" : "auto",
			}}
			onClick={onClick}>
			{disabled && (
				<Box
					sx={{
						top: "0px",
						left: "0px",
						position: "absolute",
						backgroundColor: "rgba(0,0,0,.04)",
						backdropFilter: "saturate(0.1)",
						width: "100%",
						height: "100%",
						borderRadius: "6px",
						zIndex: 1,
					}}
				/>
			)}

			{icon && (
				<Box
					sx={{
						alignItems: "center",
						lineHeight: "0px",
					}}>
					{icon}
				</Box>
			)}

			{content}

			{iconRight && (
				<>
					{!center && <Spacer grow />}
					<Box sx={{ lineHeight: "0px" }}>{iconRight}</Box>
				</>
			)}
		</Box>
	);
};

export default Button;
